"use client"

import { usePathname } from "next/navigation"
import { KeyboardShortcutProvider } from "@/contexts/keyboard-shortcut-context"
import { useAuth } from "@clerk/nextjs"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Toaster, TooltipProvider } from "@ui/shad"
import { HotkeysProvider } from "react-hotkeys-hook"

import { isPublicIntakeFormRoute } from "@/lib/public-intake-form-route"
import { GlobalConfetti } from "@/components/confetti/global-confetti"
import GlobalDialog from "@/components/dialogs/global"
import GlobalSlideover from "@/components/dialogs/global-slideover"
import PinnedDialog from "@/components/dialogs/pinned"
import GlobalHotkeys from "@/components/hotkeys/global"
import { InactivityLock } from "@/components/layout/inactivity-lock"
import GlobalMeetingRecording from "@/components/meetings/global-recording"
import { OnboardingGate } from "@/components/onboarding/onboarding-gate"
import { ImpersonationBanner } from "@/components/organizations/impersonation-banner"
import { CommandPaletteWrapper } from "@/components/providers/command-palette-wrapper"
import { SentryUserIdentifier } from "@/components/sentry/sentry-user-identifier"
import { SettingsReturnPathTracker } from "@/components/settings/settings-return-path"
import GlobalVoiceTranscription from "@/components/voice-transcription/global"

import GlobalQueriesWrapper from "../global-queries-wrapper"
import { IntercomProvider } from "./intercom"
import { PostHogProvider } from "./posthog"
import ReactQueryProvider from "./react-query-provider"

interface ProvidersProps {
  children: React.ReactNode
}

export default function Providers({ children }: ProvidersProps) {
  const pathname = usePathname()
  const { sessionId } = useAuth()
  const isIntakeFormRoute = isPublicIntakeFormRoute(pathname)
  const isCheckoutRoute = pathname === "/checkout" || pathname.startsWith("/checkout/")
  const isBackOfficeRoute = pathname.startsWith("/back-offices")
  const isPublicRoute =
    pathname === "/series-a-love" ||
    pathname === "/" ||
    pathname.startsWith("/privacy-policy") ||
    pathname.startsWith("/terms-of-service") ||
    pathname.startsWith("/email/unsubscribe") ||
    pathname.startsWith("/portal") ||
    isCheckoutRoute ||
    isIntakeFormRoute

  const content = isPublicRoute ? (
    children
  ) : isBackOfficeRoute ? (
    <>
      <SentryUserIdentifier />
      <InactivityLock />
      {children}
    </>
  ) : (
    <IntercomProvider>
      <HotkeysProvider initiallyActiveScopes={["*"]}>
        <KeyboardShortcutProvider>
          <CommandPaletteWrapper>
            <SentryUserIdentifier />
            <GlobalQueriesWrapper />
            <PinnedDialog />
            <GlobalDialog />
            <GlobalSlideover />
            <GlobalHotkeys />
            <GlobalVoiceTranscription />
            <GlobalMeetingRecording />
            <GlobalConfetti />
            <InactivityLock />
            <SettingsReturnPathTracker />
            <OnboardingGate>{children}</OnboardingGate>
            <ReactQueryDevtools buttonPosition="bottom-left" position="left" />
          </CommandPaletteWrapper>
        </KeyboardShortcutProvider>
      </HotkeysProvider>
    </IntercomProvider>
  )

  return (
    <>
      <ReactQueryProvider key={sessionId || "signed-out"} persistUserCache={!isIntakeFormRoute && !isCheckoutRoute}>
        <TooltipProvider delayDuration={300}>
          <ImpersonationBanner />
          {isIntakeFormRoute || isCheckoutRoute ? content : <PostHogProvider>{content}</PostHogProvider>}
        </TooltipProvider>
      </ReactQueryProvider>
      <Toaster />
    </>
  )
}
